declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim();

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!SITE_KEY) {
    return Promise.resolve();
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve();
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-recaptcha-v3="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.dataset.recaptchaV3 = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

export async function getRecaptchaToken(action: string): Promise<string | null> {
  if (typeof window === 'undefined' || !SITE_KEY) {
    return null;
  }

  await loadScript();

  if (!window.grecaptcha?.ready) {
    return null;
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      window.grecaptcha!
        .execute(SITE_KEY, { action })
        .then(resolve)
        .catch(reject);
    });
  });
}

export function hasRecaptchaSiteKey(): boolean {
  return Boolean(SITE_KEY);
}
